import React from "react";
import { useIntl } from "react-intl";
import { Box, Paper, CircularProgress, IconButton, Chip, useTheme } from "@mui/material";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from '@mui/x-data-grid/locales';
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import FlakyOutlinedIcon from "@mui/icons-material/FlakyOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { tokens } from "../../../assets/theme";
import messages from "./messages";

export default function MachineSectionsGrid(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const apiRef = useGridApiRef();
    const { formatMessage } = useIntl();
    const language = localStorage.getItem('language') || 'en';
    const translations = language === "sv" ? svSE : enUS;

    function isLight(color) {
        if (color?.length === 7) {
            const rgb = [
                parseInt(color.substring(1, 3), 16),
                parseInt(color.substring(3, 5), 16),
                parseInt(color.substring(5), 16),
            ];
            const luminance =
                (0.2126 * rgb[0]) / 255 +
                (0.7152 * rgb[1]) / 255 +
                (0.0722 * rgb[2]) / 255;
            return luminance > 0.5;
        }
        return false;
    }

    const columns = [
        {
            field: "name",
            headerName: formatMessage(messages.nameColumn),
            flex: 1,
            minWidth: 180,
        },
        {
            field: "code",
            headerName: formatMessage(messages.codeColumn),
            flex: 0.4,
            minWidth: 100,
        },
        {
            field: "sortOrder",
            headerName: formatMessage(messages.sortOrderColumn),
            flex: 0.3,
            minWidth: 100,
        },
        {
            field: "stopReasonCodeIds",
            headerName: formatMessage(messages.stopReasonCodesColumn),
            flex: 1.5,
            minWidth: 260,
            sortable: false,
            renderCell: (params) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "4px", py: "6px" }}>
                    {(params.value ?? []).map((id) => {
                        const stopReasonCode = props.stopReasonCodes.find((s) => s.id === id);
                        if (!stopReasonCode) {
                            return null;
                        }
                        return (
                            <Chip
                                key={id}
                                size="small"
                                label={stopReasonCode.name}
                                sx={{
                                    backgroundColor: stopReasonCode.colorCode,
                                    color: isLight(stopReasonCode.colorCode) ? colors.gray[900] : colors.gray[100],
                                }}
                            />
                        );
                    })}
                </Box>
            ),
        },
        {
            field: "edit",
            headerName: "",
            sortable: false,
            renderCell: (params) => (
                <>
                    <IconButton
                        aria-label={formatMessage(messages.editMachineSectionTitle)}
                        size="small"
                        sx={{
                            "&:hover": { backgroundColor: colors.primary[500] },
                            backgroundColor: colors.primary[700],
                        }}
                        onClick={() => props.handleEditMachineSectionClick(params.row.id)}
                    >
                        <RemoveRedEyeOutlinedIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                        aria-label={formatMessage(messages.editStopReasonCodesButtonLabel)}
                        size="small"
                        sx={{
                            "&:hover": { backgroundColor: colors.primary[500] },
                            backgroundColor: colors.primary[700],
                            marginLeft: "10px"
                        }}
                        onClick={() => props.handleEditStopReasonCodesClick(params.row.id)}
                    >
                        <FlakyOutlinedIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                        aria-label={formatMessage(messages.deleteMachineSectionTitle)}
                        size="small"
                        sx={{
                            "&:hover": { backgroundColor: colors.primary[500] },
                            backgroundColor: colors.primary[700],
                            marginLeft: "10px"
                        }}
                        onClick={() => props.handleDeleteMachineSectionClick(params.row.id)}
                    >
                        <DeleteIcon fontSize="inherit" />
                    </IconButton>
                </>
            ),
            width: 140,
        },
    ];

    if (props.isLoading) {
        return (
            <Box padding={2}>
                <Paper sx={{ position: "relative", borderRadius: "4px" }}>
                    <CircularProgress color="secondary" />
                </Paper>
            </Box>
        );
    }

    return (
        <Box padding={2} paddingTop={8}>
            <Paper
                sx={{
                    position: "relative",
                    borderRadius: "4px",
                    border: `1px solid ${colors.primary[700]}`,
                    overflow: "hidden",
                }}
            >
                <Box
                    m="0px 0 0 0"
                    height="70vh"
                    sx={{
                        "& .MuiDataGrid-root": {
                            backgroundColor: colors.primary[400],
                            borderColor: `${colors.primary[700]} !important`,
                            borderRadius: "0px",
                            fontSize: "1.0rem",
                            border: "0 !important",
                        },
                        "& .MuiDataGrid-main": {
                            borderRadius: "0px",
                            border: "0 !important",
                            borderColor: `${colors.primary[700]} !important`,
                        },
                        "& .MuiDataGrid-cell": {
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px !important",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.primary[400],
                            fontSize: '1.0rem',
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px"
                        },
                        "& .MuiDataGrid-virtualScroller": {
                            backgroundColor: colors.primary[400],
                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "none",
                            backgroundColor: colors.primary[600],
                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                            color: `${colors.gray[100]} !important`,
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                            opacity: 0,
                            transition: "opacity 0.2s"
                        },
                        "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": {
                            opacity: 1
                        },
                    }}
                >
                    <DataGridPro
                        apiRef={apiRef}
                        localeText={translations.components.MuiDataGrid.defaultProps.localeText}
                        rows={props.machineSections}
                        getRowId={(row) => row.id}
                        columns={columns}
                        density="standard"
                        getRowHeight={() => "auto"}
                        getEstimatedRowHeight={() => 44}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
